import { configToStyle, tripletToHex, contrastSummary } from "@/lib/appearance";
import { cn } from "@/lib/utils";
import { Check, Copy, Pencil, Archive, Eye, Star, Building2, Download, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

function Swatches({ config }) {
  const light = config?.light || {};
  const keys = ["primary", "accent", "secondary", "background", "foreground"];
  return (
    <div className="flex gap-1.5">
      {keys.map((k) => (
        <span key={k} className="h-5 w-5 rounded-full border border-black/10"
          style={{ background: `rgb(${light[k] || "0 0 0"})` }} title={k} />
      ))}
    </div>
  );
}

export function PresetGallery({
  themes, activeThemeId, orgThemeId, isAdmin,
  onApply, onSetOrg, onDuplicate, onEdit, onArchive, onPreview, onExport,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="preset-gallery">
      {themes.map((t) => {
        const isActive = t.id === activeThemeId;
        const isOrg = t.id === orgThemeId;
        const style = configToStyle(t.config, "light");
        const { lightFails, darkFails } = contrastSummary(t.config);
        const aaOk = lightFails + darkFails === 0;
        return (
          <div key={t.id} data-testid={`theme-card-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={cn("group overflow-hidden rounded-xl border shadow-soft transition-colors",
              isActive ? "border-accent ring-1 ring-accent" : "border-border")}>
            {/* mini preview */}
            <div className="relative h-24 p-3" style={style}>
              <div className="absolute inset-0 opacity-90 bg-gradient-hero" style={{ backgroundImage: "var(--gradient-hero)" }} />
              <div className="relative flex h-full flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ background: `rgb(${t.config?.light?.accent})`, color: `rgb(${t.config?.light?.["accent-foreground"]})` }}>
                    Aa
                  </span>
                  {isOrg && <span className="flex items-center gap-1 rounded-full bg-black/30 px-2 py-0.5 text-[10px] text-white"><Building2 className="h-3 w-3" /> Org</span>}
                </div>
                <div className="rounded-lg p-2" style={{ background: `rgb(${t.config?.light?.card})` }}>
                  <div className="h-1.5 w-16 rounded-full" style={{ background: `rgb(${t.config?.light?.primary})` }} />
                  <div className="mt-1 h-1.5 w-10 rounded-full" style={{ background: `rgb(${t.config?.light?.muted})` }} />
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="truncate font-heading text-lg font-semibold text-foreground">{t.name}</h4>
                    {t.is_preset && <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-secondary-foreground">Preset</span>}
                    <span className={cn("flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold",
                      aaOk ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700")}
                      title={aaOk ? "Meets WCAG AA in light & dark" : `${lightFails + darkFails} contrast pair(s) fail AA`}
                      data-testid={`aa-summary-${t.id}`}>
                      {aaOk ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />} {aaOk ? "AA" : `${lightFails + darkFails}`}
                    </span>
                  </div>
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{t.description}</p>
                </div>
                <Swatches config={t.config} />
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Button size="sm" variant={isActive ? "default" : "outline"} className="h-8 gap-1.5"
                  onClick={() => onApply(t)} data-testid={`apply-theme-${t.id}`}>
                  {isActive ? <><Check className="h-3.5 w-3.5" /> Applied</> : "Apply"}
                </Button>
                <Button size="sm" variant="ghost" className="h-8 gap-1.5" onClick={() => onPreview(t)} data-testid={`preview-theme-${t.id}`}>
                  <Eye className="h-3.5 w-3.5" /> Preview
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Export theme"
                  onClick={() => onExport?.(t)} data-testid={`export-${t.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
                {isAdmin && (
                  <>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Set as org default"
                      onClick={() => onSetOrg(t)} data-testid={`set-org-${t.id}`}>
                      <Star className={cn("h-3.5 w-3.5", isOrg && "fill-accent text-accent")} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Duplicate"
                      onClick={() => onDuplicate(t)} data-testid={`duplicate-${t.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    {!t.is_preset && (
                      <>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Edit"
                          onClick={() => onEdit(t)} data-testid={`edit-${t.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" title="Archive"
                          onClick={() => onArchive(t)} data-testid={`archive-${t.id}`}>
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
